import React, {useState, useRef} from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  InputGroup,
  Card,
  Row,
  Col,
  Badge,
  Tabs,
  Tab,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  FaQrcode,
  FaCopy,
  FaDownload,
  FaShare,
  FaWhatsapp,
  FaEnvelope,
  FaLink,
  FaPrint,
  FaCog,
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle,
  FaInfoCircle,
  FaMobile,
  FaDesktop,
} from "react-icons/fa";
import {QRCodeSVG} from "qrcode.react";

const QRInviteGenerator = ({show, onHide, audienceName, audienceId}) => {
  const [inviteUrl, setInviteUrl] = useState("");
  const [qrSize, setQrSize] = useState(256);
  const [includeExpiry, setIncludeExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const qrRef = useRef();

  React.useEffect(() => {
    if (show && audienceId) {
      // Generar URL de invitación optimizada para WhatsApp Business
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/subscribe/${audienceId}`;
      setInviteUrl(url);

      // Mensaje optimizado para conversión
      setCustomMessage(
        `🚚 ¡Únete a la lista "${audienceName}"!\n\n✅ Recibe notificaciones importantes\n✅ Información de rutas y trabajos\n✅ Comunicación directa con coordinadores\n\n👆 Escanea el QR o usa el enlace para suscribirte GRATIS`
      );
    }
  }, [show, audienceId, audienceName]);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback para navegadores que no soportan clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = inviteUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (canvas) {
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `QR_${audienceName.replace(/\s+/g, "_")}_${
        new Date().toISOString().split("T")[0]
      }.png`;
      link.href = url;
      link.click();
    }
  };

  const handleShareWhatsApp = () => {
    const message = encodeURIComponent(`${customMessage}\n\n🔗 ${inviteUrl}`);
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(
      `Invitación: ${audienceName} - Comunicaciones WhatsApp`
    );
    const body = encodeURIComponent(
      `${customMessage.replace(
        /\n/g,
        "%0D%0A"
      )}\n\n🔗 Enlace de suscripción: ${inviteUrl}\n\n---\nEste enlace te permite suscribirte de forma gratuita y segura.`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const canvas = qrRef.current?.querySelector("canvas");
    if (canvas && printWindow) {
      const qrDataUrl = canvas.toDataURL("image/png");
      printWindow.document.write(`
        <html>
          <head>
            <title>Código QR - ${audienceName}</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                text-align: center; 
                padding: 40px 20px; 
                max-width: 600px;
                margin: 0 auto;
              }
              .header {
                border-bottom: 3px solid #0d6efd;
                padding-bottom: 20px;
                margin-bottom: 30px;
              }
              .qr-container { 
                margin: 30px 0; 
                padding: 20px;
                border: 2px dashed #ccc;
                border-radius: 10px;
              }
              .message { 
                max-width: 500px; 
                margin: 20px auto; 
                line-height: 1.6;
                font-size: 16px;
                color: #333;
              }
              .url {
                background: #f8f9fa;
                padding: 15px;
                border-radius: 8px;
                word-break: break-all;
                font-family: monospace;
                margin: 20px 0;
              }
              .instructions {
                background: #e7f3ff;
                padding: 20px;
                border-radius: 8px;
                margin-top: 30px;
                text-align: left;
              }
              .instructions h3 {
                color: #0d6efd;
                margin-top: 0;
              }
              .instructions ol {
                margin: 10px 0;
              }
              .instructions li {
                margin: 8px 0;
              }
              .footer {
                margin-top: 40px;
                padding-top: 20px;
                border-top: 1px solid #ddd;
                color: #666;
                font-size: 14px;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>📱 ${audienceName}</h1>
              <p style="color: #666; font-size: 18px;">Suscripción a Comunicaciones WhatsApp</p>
            </div>
            
            <div class="message">${customMessage.replace(/\n/g, "<br>")}</div>
            
            <div class="qr-container">
              <img src="${qrDataUrl}" alt="Código QR" style="max-width: 100%;" />
              <p style="margin-top: 15px; font-weight: bold; color: #0d6efd;">
                📱 Escanea con tu cámara o WhatsApp
              </p>
            </div>
            
            <div class="url">
              <strong>🔗 Enlace directo:</strong><br>
              ${inviteUrl}
            </div>

            <div class="instructions">
              <h3>📋 Instrucciones de uso:</h3>
              <ol>
                <li><strong>Opción 1:</strong> Escanea el código QR con la cámara de tu teléfono</li>
                <li><strong>Opción 2:</strong> Abre WhatsApp → Menú → WhatsApp Web → Escanear código</li>
                <li><strong>Opción 3:</strong> Copia y pega el enlace en tu navegador</li>
                <li>Completa el formulario con tus datos</li>
                <li>¡Listo! Comenzarás a recibir comunicaciones importantes</li>
              </ol>
            </div>

            <div class="footer">
              <p>💡 <strong>Beneficio:</strong> Al suscribirte tú inicias la conversación, por lo que el servicio es completamente gratuito.</p>
              <p>🔒 Tus datos están protegidos y solo se usan para comunicaciones autorizadas.</p>
              <p>📅 Generado el: ${new Date().toLocaleDateString("es-CL")}</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handlePreviewMobile = () => {
    window.open(
      inviteUrl,
      "_blank",
      "width=375,height=667,scrollbars=yes,resizable=yes"
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaQrcode className="me-2 text-primary" />
          Generador de Invitaciones Inteligentes
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip>
                Optimizado para WhatsApp Business - Los usuarios inician la
                conversación = GRATIS
              </Tooltip>
            }
          >
            <FaInfoCircle
              className="ms-2 text-info"
              style={{fontSize: "0.8em"}}
            />
          </OverlayTrigger>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs defaultActiveKey="generate" className="mb-4">
          <Tab
            eventKey="generate"
            title={
              <>
                <FaQrcode className="me-1" /> Generar QR
              </>
            }
          >
            <Row>
              <Col lg={5}>
                <Card className="border-0 bg-light h-100">
                  <Card.Body className="text-center d-flex flex-column justify-content-center">
                    <h6 className="fw-bold mb-3">Vista Previa del QR</h6>
                    <div ref={qrRef} className="mb-3" id="qr-code-svg">
                      <QRCodeSVG
                        value={inviteUrl}
                        size={qrSize}
                        level="H" // Alta corrección de errores
                        includeMargin={true}
                        renderAs="canvas"
                        fgColor="#000000"
                        bgColor="#FFFFFF"
                      />
                    </div>
                    <Badge bg="info" className="mb-2">
                      {qrSize}x{qrSize} px - Alta calidad
                    </Badge>
                    <div className="mt-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handlePreviewMobile}
                        className="me-2"
                      >
                        <FaMobile className="me-1" />
                        Vista Móvil
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => window.open(inviteUrl, "_blank")}
                      >
                        <FaDesktop className="me-1" />
                        Vista Desktop
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={7}>
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaCog className="me-2" />
                          Tamaño del QR
                        </Form.Label>
                        <Form.Select
                          value={qrSize}
                          onChange={(e) => setQrSize(parseInt(e.target.value))}
                        >
                          <option value={128}>
                            Pequeño - 128px (redes sociales)
                          </option>
                          <option value={256}>
                            Mediano - 256px (recomendado)
                          </option>
                          <option value={512}>
                            Grande - 512px (impresión)
                          </option>
                          <option value={1024}>
                            Extra Grande - 1024px (banners)
                          </option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaCalendarAlt className="me-2" />
                          Configuración
                        </Form.Label>
                        <Form.Check
                          type="checkbox"
                          label="Incluir fecha de expiración"
                          checked={includeExpiry}
                          onChange={(e) => setIncludeExpiry(e.target.checked)}
                        />
                        {includeExpiry && (
                          <Form.Control
                            type="date"
                            className="mt-2"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(e.target.value)}
                            min={new Date().toISOString().split("T")[0]}
                          />
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">
                      <FaWhatsapp className="me-2 text-success" />
                      Mensaje de Invitación
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Mensaje que acompañará la invitación..."
                    />
                    <Form.Text className="text-muted">
                      💡 Tip: Usa emojis y beneficios claros para aumentar la
                      conversión
                    </Form.Text>
                  </Form.Group>

                  <Alert variant="info" className="small">
                    <FaCheckCircle className="me-2" />
                    <strong>URL generada:</strong> {inviteUrl}
                  </Alert>
                </Form>
              </Col>
            </Row>
          </Tab>

          <Tab
            eventKey="share"
            title={
              <>
                <FaShare className="me-1" /> Compartir
              </>
            }
          >
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">
                <FaLink className="me-2" />
                Enlace de Suscripción Optimizado
              </Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={inviteUrl}
                  readOnly
                  className="font-monospace"
                />
                <Button
                  variant={copied ? "success" : "outline-secondary"}
                  onClick={handleCopyUrl}
                >
                  {copied ? (
                    <FaCheckCircle className="me-1" />
                  ) : (
                    <FaCopy className="me-1" />
                  )}
                  {copied ? "¡Copiado!" : "Copiar"}
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Este enlace lleva a una página optimizada para móviles con
                formulario de suscripción
              </Form.Text>
            </Form.Group>

            <Row className="g-3 mb-4">
              <Col md={4}>
                <Button
                  variant="success"
                  className="w-100 py-3"
                  onClick={handleShareWhatsApp}
                >
                  <FaWhatsapp className="me-2" size={20} />
                  <div>
                    <div>Compartir por WhatsApp</div>
                    <small className="opacity-75">Alcance directo</small>
                  </div>
                </Button>
              </Col>
              <Col md={4}>
                <Button
                  variant="primary"
                  className="w-100 py-3"
                  onClick={handleShareEmail}
                >
                  <FaEnvelope className="me-2" size={20} />
                  <div>
                    <div>Compartir por Email</div>
                    <small className="opacity-75">Formal y detallado</small>
                  </div>
                </Button>
              </Col>
              <Col md={4}>
                <Button
                  variant="outline-secondary"
                  className="w-100 py-3"
                  onClick={() => {
                    const text = `${customMessage}\n\n🔗 ${inviteUrl}`;
                    if (navigator.share) {
                      navigator.share({
                        title: `Invitación: ${audienceName}`,
                        text: text,
                        url: inviteUrl,
                      });
                    } else {
                      handleCopyUrl();
                    }
                  }}
                >
                  <FaShare className="me-2" size={20} />
                  <div>
                    <div>Compartir Nativo</div>
                    <small className="opacity-75">Redes sociales</small>
                  </div>
                </Button>
              </Col>
            </Row>
          </Tab>

          <Tab
            eventKey="analytics"
            title={
              <>
                <FaChartLine className="me-1" /> Estadísticas
              </>
            }
          >
            <Alert variant="warning" className="mb-4">
              <FaChartLine className="me-2" />
              <strong>Panel de Analíticas (Próximamente):</strong> Aquí podrás
              ver métricas detalladas de rendimiento de tus invitaciones.
            </Alert>

            <Row className="text-center mb-4">
              <Col md={3}>
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <h3 className="fw-bold text-primary">0</h3>
                    <small className="text-muted">Escaneos QR</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <h3 className="fw-bold text-info">0</h3>
                    <small className="text-muted">Clicks en enlace</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <h3 className="fw-bold text-success">0</h3>
                    <small className="text-muted">Suscripciones</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-light">
                  <Card.Body>
                    <h3 className="fw-bold text-warning">0%</h3>
                    <small className="text-muted">Tasa conversión</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="border-0 bg-light">
              <Card.Body>
                <h6 className="fw-bold mb-3">Métricas que podrás ver:</h6>
                <Row>
                  <Col md={6}>
                    <ul className="small">
                      <li>📱 Escaneos por dispositivo (iOS/Android)</li>
                      <li>🌍 Ubicación geográfica de accesos</li>
                      <li>⏰ Horarios de mayor actividad</li>
                      <li>📊 Fuentes de tráfico más efectivas</li>
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="small">
                      <li>💰 Ahorro estimado en costos WhatsApp</li>
                      <li>📈 Tendencias de suscripción</li>
                      <li>🎯 Tasa de conversión por canal</li>
                      <li>📋 Datos demográficos de suscriptores</li>
                    </ul>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            <Button
              variant="outline-secondary"
              onClick={handlePrint}
              className="me-2"
            >
              <FaPrint className="me-1" />
              Imprimir QR
            </Button>
            <Button variant="outline-primary" onClick={handleDownloadQR}>
              <FaDownload className="me-1" />
              Descargar PNG
            </Button>
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Cerrar
            </Button>
            <Button variant="success" onClick={handleCopyUrl}>
              <FaLink className="me-1" />
              {copied ? "¡Copiado!" : "Copiar Enlace"}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default QRInviteGenerator;
